/**
 * River tile atlas — v0M14 tile-based river renderer.
 *
 * Replaces the MFix10.3 cardinal-spline `drawRivers` overlay with an
 * authored-tile approach. For each River cell (terrain id 9), the
 * renderer inspects the 4 orthogonal neighbours; the bitmask of
 * "which neighbours are also River" picks the matching tile shape.
 *
 * Bitmask encoding (N=1, E=2, S=4, W=8):
 *   0b0101 (5)  = N+S       → straight NS       → river-ns.png
 *   0b1010 (10) = E+W       → straight EW       → river-ew.png
 *   0b0011 (3)  = N+E       → bend NE           → river-ne.png
 *   0b1001 (9)  = N+W       → bend NW           → river-nw.png
 *   0b0110 (6)  = S+E       → bend SE           → river-se.png
 *   0b1100 (12) = S+W       → bend SW           → river-sw.png
 *   0b0111 (7)  = N+E+S     → T-tee (missing W) → river-nes.png
 *   0b1101 (13) = N+S+W     → T-tee (missing E) → river-nsw.png
 *   0b1011 (11) = N+E+W     → T-tee (missing S) → river-new.png
 *   0b1110 (14) = S+E+W     → T-tee (missing N) → river-sew.png
 *   0b1111 (15) = N+E+S+W   → cross             → river-nesw.png
 *
 * Endpoints (single-arm bitmasks 1/2/4/8) — v0M14 ships placeholder
 * fallback to the straight tile in the arm's axis (dangling half-line
 * ends mid-cell in grass). v0M14.3a replaces with authored source +
 * mouth tiles once Michael delivers them.
 *
 * Isolated River cells (bitmask 0) — should not occur per the map
 * generator; logs a dev warning and skips rendering.
 *
 * Per design lock (session 35, v0M14.0): sea/lake neighbours do NOT
 * count as River arms — treated same as grass. A River cell against
 * the coast reads as a mouth endpoint. The sea-adjacency check is
 * used only to distinguish source vs mouth tiles (v0M14.3a work).
 */

/** Bitmask arm encoding — matches renderer.ts::drawRiverTiles. */
export const ARM_N = 0b0001;
export const ARM_E = 0b0010;
export const ARM_S = 0b0100;
export const ARM_W = 0b1000;

const TILE_FILES: Readonly<Record<number, string>> = {
  [ARM_N | ARM_S]:                'river-ns.png',
  [ARM_E | ARM_W]:                'river-ew.png',
  [ARM_N | ARM_E]:                'river-ne.png',
  [ARM_N | ARM_W]:                'river-nw.png',
  [ARM_S | ARM_E]:                'river-se.png',
  [ARM_S | ARM_W]:                'river-sw.png',
  [ARM_N | ARM_E | ARM_S]:        'river-nes.png',
  [ARM_N | ARM_S | ARM_W]:        'river-nsw.png',
  [ARM_N | ARM_E | ARM_W]:        'river-new.png',
  [ARM_S | ARM_E | ARM_W]:        'river-sew.png',
  [ARM_N | ARM_E | ARM_S | ARM_W]: 'river-nesw.png',
};

/**
 * Endpoint tiles — v0M14.3a: authored source + mouth tiles landed.
 * `source` is a 1-arm cell with no sea-adjacent neighbour (spring pond
 * with river flowing out); `mouth` is a 1-arm cell adjacent to sea
 * (river empties into a delta pool).
 *
 * Stored canonical orientation:
 *   source → arm at S (pond upper, arm exits south-centre edge)
 *   mouth  → arm at N (thin arm enters north-centre, delta pool lower)
 *
 * The loader pre-rotates each to cover the other 3 arm directions:
 *   source arm-S → 180° = arm-N; 90° CW = arm-W; 90° CCW = arm-E
 *   mouth  arm-N → 180° = arm-S; 90° CW = arm-E; 90° CCW = arm-W
 */
const ENDPOINT_FILES = {
  source: 'river-source.png',  // canonical arm-S
  mouth:  'river-mouth.png',   // canonical arm-N
} as const;

export interface RiverTileAtlas {
  /**
   * Look up the tile canvas for a given neighbour bitmask.
   * Returns null for isolated cells (bitmask 0) — caller skips render.
   *
   * `isSeaAdjacent` used only for 1-arm endpoint cells (bitmask
   * ARM_N/E/S/W): true → mouth tile (river empties into sea/lake);
   * false → source tile (spring pond / interior terminus).
   */
  pickTile(bitmask: number, isSeaAdjacent?: boolean): HTMLCanvasElement | null;
}

/**
 * Kick off image loads in parallel. Resolves with an atlas once every
 * tile loads. Rejects if any REQUIRED tile fails so the renderer takes
 * the fallback path (spline renderer, still present until v0M14.3) —
 * see main.ts wiring.
 */
export async function loadRiverTileAtlas(): Promise<RiverTileAtlas | null> {
  const tiles: Record<number, HTMLCanvasElement> = {};
  // Endpoint sub-maps keyed by arm bitmask (ARM_N/E/S/W). Pre-rotated
  // at load time from the canonical S-arm source / N-arm mouth tiles.
  const sourceByArm: Record<number, HTMLCanvasElement> = {};
  const mouthByArm: Record<number, HTMLCanvasElement> = {};

  try {
    for (const [maskStr, filename] of Object.entries(TILE_FILES)) {
      const mask = Number(maskStr);
      const img = await loadImage(`/assets/terrain/rivers/${filename}`);
      tiles[mask] = imageToCanvas(img);
    }
    const sourceImg = await loadImage(`/assets/terrain/rivers/${ENDPOINT_FILES.source}`);
    const mouthImg = await loadImage(`/assets/terrain/rivers/${ENDPOINT_FILES.mouth}`);
    const sourceBase = imageToCanvas(sourceImg);  // canonical arm-S
    const mouthBase = imageToCanvas(mouthImg);    // canonical arm-N

    // Source (canonical arm-S). Rotation math: 90° CW moves arm S→W;
    // 180° moves arm S→N; 90° CCW (= 270° CW) moves arm S→E.
    sourceByArm[ARM_S] = sourceBase;
    sourceByArm[ARM_W] = rotateCanvas(sourceBase, 90);
    sourceByArm[ARM_N] = rotateCanvas(sourceBase, 180);
    sourceByArm[ARM_E] = rotateCanvas(sourceBase, 270);

    // Mouth (canonical arm-N). 90° CW moves arm N→E; 180° moves N→S;
    // 90° CCW moves N→W.
    mouthByArm[ARM_N] = mouthBase;
    mouthByArm[ARM_E] = rotateCanvas(mouthBase, 90);
    mouthByArm[ARM_S] = rotateCanvas(mouthBase, 180);
    mouthByArm[ARM_W] = rotateCanvas(mouthBase, 270);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[SU] river tile atlas load failed: ${err instanceof Error ? err.message : String(err)}`);
    return null;
  }

  // eslint-disable-next-line no-console
  console.info(
    `[SU] river tile atlas loaded: ${Object.keys(tiles).length} shape tiles + source/mouth endpoints (4 arm rotations each)`,
  );

  return {
    pickTile(bitmask: number, isSeaAdjacent = false): HTMLCanvasElement | null {
      if (bitmask === 0) {
        // eslint-disable-next-line no-console
        console.warn(`[SU] Isolated River cell (bitmask 0) — generator bug`);
        return null;
      }
      // 1-arm endpoint: source (interior) or mouth (sea-adjacent).
      if (bitmask === ARM_N || bitmask === ARM_E || bitmask === ARM_S || bitmask === ARM_W) {
        const bank = isSeaAdjacent ? mouthByArm : sourceByArm;
        return bank[bitmask] ?? null;
      }
      return tiles[bitmask] ?? null;
    },
  };
}

/**
 * Rotate a square source canvas by 90/180/270 degrees clockwise.
 * Returns a new canvas of the same size. Used to expand the canonical
 * source/mouth tile into all 4 arm-direction orientations.
 */
function rotateCanvas(src: HTMLCanvasElement, degrees: 90 | 180 | 270): HTMLCanvasElement {
  const size = src.width;
  const out = document.createElement('canvas');
  out.width = size;
  out.height = size;
  const ctx = out.getContext('2d');
  if (!ctx) throw new Error('rotateCanvas: 2D context unavailable');
  ctx.translate(size / 2, size / 2);
  ctx.rotate((degrees * Math.PI) / 180);
  ctx.drawImage(src, -size / 2, -size / 2);
  return out;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load ${src}`));
    img.src = src;
  });
}

function imageToCanvas(img: HTMLImageElement): HTMLCanvasElement {
  const off = document.createElement('canvas');
  off.width = img.naturalWidth;
  off.height = img.naturalHeight;
  const ctx = off.getContext('2d');
  if (!ctx) throw new Error('imageToCanvas: 2D context unavailable');
  ctx.drawImage(img, 0, 0);
  return off;
}
